import { IProduct } from '@monofront/types';

export class WeakProductCache {
  // A bit of vocabulary:
  // - "value"/"reference": to try to keep it short, JavaScript uses "references" for any non-primitive values.
  // Primitives are: numbers, strings, booleans, null, undefined, symbols, bigints.
  // Non-primitives are: objects, arrays and functions.
  // When passing a non-primitive value to a function, we pass a reference to it, which means we manipulate
  // the same object in memory.
  // When passing a primitive value, JS makes a copy of it, and we can't have references for primitives.
  // - A "strong reference" is a reference that prevents the garbage collector from collecting an object.
  // This the default way of holding a reference, this means that anytimes we manipulate an object (an
  // object, an array or a function), we hold a strong reference to it.
  // - A "weak reference" is a reference that doesn't prevent the garbage collector from collecting an object.
  // If an object is only reachable by weak references, it will be collected.
  // - A "garbage collector" is a part of garbage collected language (like JavaScript, Java, Go, C#...)
  // that is responsible for freeing memory no longer used by the program. It "collects" objects that
  // are not "reachable" by the program anymore.
  // - A non-reachable object is an object that cannot be accessed in the program anymore.
  // Quick example:
  // ```
  // function fun() {
  //   const obj = { a: 1 };
  // }
  //
  // fun()
  // // At this point, `obj` is not reachable anymore, because it was only accessible inside `fun`,
  // // -> it will be garbage collected, and its memory reclaimed.
  // ```
  // - Non-garbage collected languages includes C, C++, Rust, Swift, Objective-C. These often requires
  // manual memory management from the developers, but the memory cleaning is managed by the OS directly.
  //
  // `WeakMap` exists, but it holds the value strongly: it's the key that is held weakly instead, key that
  // can't be a primitive value*. This is why we use a classic `Map` with `WeakRef` for values.
  //
  // The MDN doc is very cautious on the use of all memory related features (which is weird because it's
  // a common pattern in other languages), but we are sure we want to use WeakRef here, because:
  // - Products are rather big objects
  // - We are in a SPA or a long lived app, and we don't want to accumulate all the catalog in memory
  // over every page change.
  // - The cache goal is to prevent Algolia requests in operations where we already have the product in
  // memory, like the add to cart.
  //
  // *: the key can't be a primitive value because it would be impossible to hold a weak reference to it.
  // Either it is a literal and the value will be held forever, or it will dynamic and the value will be
  // different each time we want to access the map.
  private cacheMap = new Map<string, WeakRef<IProduct>>();

  constructor(
    // `FinalizationRegistry` enables us to observes when an object is garbage collected, so we can
    // delete the corresponding entry in the map.
    // The standard guarantee us that when the finalizer callback is called for an object, any weak reference
    // to this object will dereference to `undefined`
    private finalizer = new FinalizationRegistry((key: string) => {
      this.cacheMap.delete(key);
    }),
  ) {}

  set(key: string, value: IProduct) {
    const cachedValue = this.get(key);

    if (cachedValue) {
      if (cachedValue !== value) {
        // If we already had a value for this key, we stop observing the old value
        this.finalizer.unregister(cachedValue);
      }

      return;
    }

    this.cacheMap.set(key, new WeakRef(value));
    this.finalizer.register(value, key, value);
  }

  get(key: string) {
    // When we "deref" a weak reference, we get a new strong reference to the value,
    // or `undefined` if the value has already been garbage collected.
    return this.cacheMap.get(key)?.deref() ?? undefined;
  }

  /**
   * `WeakRef` is not supported by React Native (hermes), unless running in new architecture
   * and RN >0.74.
   * `FinalizationRegistry` is unsupported by React Native.
   */
  static isSupportedByCurrentPlatform() {
    return (
      typeof WeakRef !== 'undefined' &&
      typeof FinalizationRegistry !== 'undefined'
    );
  }
}
